import type {
  SyncOperation,
  SyncPullResponse,
  SyncPushResponse,
  SyncReplayEvent,
} from "@stockline/shared/types/sync"
import type { ChangeQueue } from "./change-queue"
import type {
  HttpClientAdapter,
  OfflineResourceConfig,
  RealtimeTransportAdapter,
  ResourcePriority,
  StorageAdapter,
  SyncMeta,
  SyncStatus,
  TelemetrySink,
  TransportEventHandler,
} from "./types"
import { runHotEviction, type HotEvictionDeps } from "./eviction"
import {
  fetchOnDemandWindow as runOnDemandWindow,
  type OnDemandDeps,
  type OnDemandResult,
  type OnDemandWindow,
} from "./on-demand"
import {
  fetchEmailBody as runFetchEmailBody,
  type OnDemandEmailDeps,
  type FetchEmailBodyResult,
} from "./on-demand-email"
import {
  fetchPreOrderById as runFetchPreOrderById,
  type FetchPreOrderByIdResult,
} from "./on-demand-preorder"
import { isDbClosedError } from "./is-db-closed-error"
import { withColdStartLock } from "./tab-leadership"

const PRIORITY_WAVES: readonly ResourcePriority[] = [
  "critical",
  "warm",
  "background",
] as const

const SYNC_META_TABLE = "_syncMeta"
const SSE_URL = "/api/v2/sync/stream"

/**
 * `_syncMeta` row id persisting the server-derived scope version (STK-1238).
 * Lives in `_syncMeta` (not localStorage) so `resetOfflineStorage` wipes it
 * atomically with the data — a fresh DB has no version, adopts the current
 * one at handshake, and never triggers a spurious wipe. The leading
 * underscore keeps it out of any real resource's namespace.
 */
const SCOPE_META_ID = "_scope"
/**
 * Coalescing window for scope-invalidation triggers. Admin flows fire several
 * membership mutations back-to-back (each publishing its own scope-changed
 * frame) — one wipe + re-pull must absorb the burst, not one per frame.
 */
const SCOPE_INVALIDATION_DEBOUNCE_MS = 300
/**
 * Hard cap on the pre-wipe change-queue flush. Mirrors `FLUSH_TIMEOUT_MS` in
 * `reset.ts`: a hanging push must not indefinitely delay dropping data the
 * viewer may no longer be allowed to hold.
 */
const SCOPE_FLUSH_TIMEOUT_MS = 3_000

/**
 * How long `runStart` waits for the auth token to appear before firing the
 * cold-start waves. The bearer is normally in localStorage by the time this
 * tab's JS runs (login/impersonation both write it before navigating), so the
 * wait resolves instantly in the common case. It only ever ticks when a cold
 * start races the token being written — the window that used to send the whole
 * first wave (one REST pull per resource) out with no `Authorization` header,
 * every one bouncing 401 before the data re-arrived over the SSE replay
 * handshake (STK-1928). Sized well under the boot watchdog stall fuse
 * (`STALL_MS` = 15s) and the cold-start lock timeout (10s): if the token never
 * lands we proceed unlocked rather than hang the boot.
 */
const AUTH_TOKEN_WAIT_TIMEOUT_MS = 5_000
/** Poll cadence for {@link AUTH_TOKEN_WAIT_TIMEOUT_MS}. */
const AUTH_TOKEN_POLL_INTERVAL_MS = 50

/**
 * Shape of the persisted `_scope` row (see SCOPE_META_ID). `pending` is the
 * durable invalidation marker: written the moment a mismatching version is
 * observed, cleared only by the completed wipe. A page killed mid-invalidation
 * (reload, navigation) leaves it behind, and the next `start()` RESUMES the
 * wipe before serving any row — without it, every boot re-detects the same
 * mismatch, re-arms the same invalidation and dies the same way (reload
 * livelock), leaving revoked rows visible indefinitely.
 */
interface ScopeMetaRow {
  id: string
  v: string
  pending?: string
}

/**
 * True for the documented transient fetch failures we want `fullFetch` to
 * retry through: Chrome's `net::ERR_NETWORK_CHANGED`, generic `Failed to
 * fetch` (DNS hiccup, socket reset), and named DOMException network errors.
 * Server errors (4xx/5xx) reach the HTTP client as response-status errors
 * with a message — those are explicitly NOT matched here so a real backend
 * bug stays loud instead of being silently retried.
 */
function isTransientFetchError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  const message = err.message
  if (err.name === "NetworkError" || err.name === "AbortError") return true
  if (message.includes("Failed to fetch")) return true
  if (message.includes("NetworkError")) return true
  if (message.includes("ERR_NETWORK_CHANGED")) return true
  if (message.includes("ERR_CONNECTION_RESET")) return true
  return false
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Parse an on-demand bound to epoch-ms, always in UTC. The order axis passes the
 * app's convention datetimes — absolute UTC+0 with NO trailing `Z`
 * (`computeUTCRangeInTz` output, e.g. `2026-07-03T00:00:00`). `Date.parse`
 * reads a *date-time* string without a zone designator as BROWSER-LOCAL, which
 * would make order-axis `depthDays` device-timezone-dependent and skew
 * `spanDays` across a DST boundary (greptile). Append `Z` to a zone-less
 * datetime so it's read as UTC. A date-ONLY string (`YYYY-MM-DD`, the delivery
 * axis) is already UTC per spec — leave it, and never append `Z` to it (invalid).
 * An already-qualified string (`Z` or a `±hh:mm` offset) is left untouched.
 */
function parseBoundMs(raw: string): number {
  const hasTime = raw.includes("T")
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)
  return Date.parse(hasTime && !hasZone ? `${raw}Z` : raw)
}

/**
 * Derive the `{ depthDays, spanDays }` shape of an on-demand window for
 * `preorders.on_demand_fetch` telemetry (STK-1924). `depthDays` = whole days
 * from the window's OLDEST bound up to `nowMs` (how far below today the recap
 * reached); `spanDays` = whole days the window covers. Both axes carry their
 * bounds under different keys (delivery = calendar dates, order = absolute-UTC
 * datetimes), unified here. Returns `null` fields for an unparseable bound
 * rather than emitting `NaN` — a loud gap in a numeric series beats a poisoned
 * average.
 *
 * `depthDays` FLOORs: the oldest bound is a midnight date but `nowMs` carries a
 * time-of-day, so rounding would tick the depth up one whole day after noon UTC
 * and make the SAME calendar window report different depths by request time
 * (cubic/greptile). Floor gives stable "whole days ago" semantics. `spanDays`
 * ROUNDs on purpose: the order axis passes inclusive day bounds
 * (`00:00:00`→`23:59:59`), so a one-day slice measures a hair under 1 day —
 * round reads it as 1, floor would under-report every span by one.
 */
export function onDemandWindowSpan(
  window: OnDemandWindow,
  nowMs: number
): { depthDays: number | null; spanDays: number | null } {
  const fromRaw = window.axis === "delivery" ? window.from : window.fromTs
  const toRaw = window.axis === "delivery" ? window.to : window.toTs
  const fromMs = parseBoundMs(fromRaw)
  const toMs = parseBoundMs(toRaw)
  return {
    depthDays: Number.isNaN(fromMs)
      ? null
      : Math.floor((nowMs - fromMs) / MS_PER_DAY),
    spanDays:
      Number.isNaN(fromMs) || Number.isNaN(toMs)
        ? null
        : Math.round((toMs - fromMs) / MS_PER_DAY),
  }
}

export interface SyncEngineOptions {
  storage: StorageAdapter
  httpClient: HttpClientAdapter
  transport: RealtimeTransportAdapter
  changeQueue: ChangeQueue
  sseUrl?: string
  getAuthToken?: () => string | null
  /** Optional operational-telemetry sink (db-closed, resync, reconnect). */
  telemetry?: TelemetrySink
}

export class SyncEngine {
  private storage: StorageAdapter
  private httpClient: HttpClientAdapter
  private transport: RealtimeTransportAdapter
  private changeQueue: ChangeQueue
  private sseUrl: string

  private resources = new Map<string, OfflineResourceConfig>()
  private _status: SyncStatus = "idle"
  private running = false
  private startGeneration = 0
  // Advisory: true while a `start()` body is executing. Read by the boot
  // watchdog so its soft-retry never re-kicks a start that is still in flight
  // (avoids overlapping cold-start waves). Purely observational — it does not
  // gate `start()` itself, whose `startGeneration` remains the cancellation
  // mechanism.
  private startInFlight = false
  private transportHandler: TransportEventHandler | null = null
  private getAuthToken: (() => string | null) | null = null
  private telemetry: TelemetrySink | null = null
  /** True once the day-roll visibility watcher is attached (once per instance). */
  private dayRollWatcherRegistered = false
  /**
   * The representative hot `windowStart` the last eviction pass reconciled.
   * Lets a same-day tab re-focus skip a redundant floor refresh; a changed
   * value signals the calendar day advanced (window slid → floor rose).
   */
  private lastEvictionWindowStart: string | null = null
  /**
   * Unsubscribe handle for the storage `onClose` watcher (registered in
   * `start`, torn down in `stop`). Null when no watcher is attached or the
   * adapter doesn't support close detection (in-memory/mock).
   */
  private closeUnsub: (() => void) | null = null
  /**
   * Guards `recoverFromUnexpectedClose` against re-entrancy — a single close
   * can fire multiple times, and a destructive cross-tab delete may close the
   * DB again while we're reopening. Held for the whole reopen+restart so a
   * flapping connection can't spin a recovery loop.
   */
  private recovering = false
  /**
   * In-memory mirror of `_syncMeta.lastSyncId` per resource. Kept up-to-date
   * by every `saveSyncMeta`/`updateSyncMeta` call. The SSE transport reads it
   * synchronously on (re)connect to build the cursor query string — Dexie
   * reads are async, the EventSource constructor is not.
   */
  private cursorCache: Record<string, number> = {}
  /**
   * Resources whose `GET /:resource/changes` resync is currently in flight.
   * Live SSE events that arrive for these resources are dropped — the GET
   * response will return them anyway, and applying them concurrently risks
   * out-of-order writes against the (about-to-be-updated) cursor.
   */
  private resyncing = new Set<string>()
  /**
   * Coalesces concurrent resync requests for the same resource: a second
   * trigger while one is in flight returns the running promise instead of
   * starting a parallel `GET /:resource/changes`.
   */
  private resyncInFlight = new Map<string, Promise<void>>()
  /**
   * Per-resource "a fresh resync was requested while one was already in flight"
   * flag. `notifyServerSyncId` / `requestResync` (push piggyback + 409 staleness
   * heal) can coalesce onto an in-flight resync whose snapshot PREDATES the
   * server state they're chasing — the coalesced promise then resolves without
   * the newer rows, silently no-op-ing the heal. Setting this flag makes the
   * in-flight resync's `finally` run one more pass from the settled cursor.
   * Cleared when consumed; a follow-up failure re-arms it so the next settle
   * retries.
   */
  private resyncRequested = new Set<string>()
  /**
   * Per-resource high-watermark for live SSE ops the `resyncing` guard dropped
   * (see the drop site in `connectTransport`'s handler). Semantics: "the cursor
   * MUST reach this syncId" — the OPPOSITE direction from `shieldRefetchFloor`'s
   * "refetch FROM here" lower bound, so the name deliberately says "watermark",
   * not "floor". When the in-flight resync settles, `resyncResource`'s `finally`
   * compares it to the resulting cursor: if the cursor never reached it (the
   * resync's snapshot predated the dropped op), a plain follow-up resync from
   * the current cursor pulls the missed op. Cleared BEFORE that follow-up fires
   * so a watermark can only be re-armed by a genuinely newer drop — never a loop.
   */
  private resyncDropWatermark = new Map<string, number>()
  /**
   * Per-resource re-fetch floor for ops the in-flight-write shield dropped in
   * `applyOperations`. Once the shielding change settles (push ack or permanent
   * failure), `recoverShieldedDrops` resyncs from this `since` to pull the
   * authoritative server state for any genuine concurrent change that was
   * dropped alongside the echo. Cleared after a successful recovery resync.
   */
  private shieldRefetchFloor = new Map<string, number>()
  /**
   * Transient in-flight shield for writes that bypass the change queue —
   * `pushBatch`'s optimistic apply writes Dexie directly (so it can reconcile
   * with the server-authoritative response), so `getActiveEntityIds` can't see
   * those rows as in-flight. The owner registers the affected ids here for the
   * POST round-trip via `shieldEntities` and clears them in `releaseEntities`
   * once it has reconciled/reverted, so a late SSE echo can't clobber the
   * optimistic row in between. Counts are per entity so overlapping batches do
   * not unshield each other. `applyOperations` consults this alongside the
   * change-queue ids; drops still record a re-fetch floor for recovery.
   */
  private transientShield = new Map<string, Map<string, number>>()
  /**
   * Single-flight for `pushChanges`. Two concurrent triggers — e.g. a user
   * write fires during a periodic sync tick, or `onReconnect()` runs while a
   * just-issued mutation is still queueing — would otherwise both call
   * `changeQueue.dequeueNext()` and race against the same Dexie row, sending
   * duplicate POSTs. The server-side V2 push endpoint is idempotent on
   * retries, but duplicate network round-trips are still wasteful (and
   * before the M3 consumer fix in 1a30d5d3f, every duplicate POST was a
   * potential duplicate-create on the ERP side).
   *
   * Concurrent callers return the same in-flight promise; the next call
   * after it resolves starts a fresh drain.
   */
  // Single-flight per drain scope. Key is `"*"` for the global drain
  // (resource=undefined) or the resource name for per-resource drains.
  // Different resources don't coalesce into one promise — fixes the
  // cubic C6 finding where a per-resource caller for "A" used to
  // return the in-flight promise for "B" and silently skip its own
  // queue until the next tick.
  private pushInFlight: Map<string, Promise<void>> = new Map()
  /**
   * Scope-invalidation state (STK-1238). `pendingScopeVersion` always holds
   * the LATEST version received (server-derived, what to persist after the
   * wipe); the timer is the coalescing window; the run promise single-flights
   * the wipe. A trigger landing mid-run leaves `pendingScopeVersion` set, and
   * the run's `finally` reschedules — so a second membership change during a
   * re-pull still gets its own invalidation instead of being lost.
   */
  private pendingScopeVersion: string | null = null
  private scopeInvalidationTimer: ReturnType<typeof setTimeout> | null = null
  private scopeInvalidationRun: Promise<void> | null = null
  /**
   * Floating apply promises spawned by the transport handler (replay
   * batches, live ops, evicts). They are NOT in `resyncInFlight`, so the
   * scope invalidation must settle them separately before wiping — a large
   * replay `putMany` landing after the wipe would resurrect stale rows AND
   * re-write the `_syncMeta` cursor, making `start()` silently skip the
   * cold re-pull (greptile P1 on PR #2618).
   */
  private transportApplyInFlight = new Set<Promise<unknown>>()

  /** Track a transport-handler apply until it settles (never rejects out). */
  private trackTransportApply(promise: Promise<unknown>): void {
    this.transportApplyInFlight.add(promise)
    const drop = () => this.transportApplyInFlight.delete(promise)
    promise.then(drop, drop)
  }

  constructor(options: SyncEngineOptions) {
    this.storage = options.storage
    this.httpClient = options.httpClient
    this.transport = options.transport
    this.changeQueue = options.changeQueue
    this.sseUrl = options.sseUrl ?? SSE_URL
    this.getAuthToken = options.getAuthToken ?? null
    this.telemetry = options.telemetry ?? null
  }

  get status(): SyncStatus {
    return this._status
  }

  /** True while a `start()` cold-start is executing (see `startInFlight`). */
  get isStarting(): boolean {
    return this.startInFlight
  }

  /**
   * Forward an operational event to the injected telemetry sink (no-op when
   * none is wired — tests/SSR). Public so the boot screen can funnel its
   * `boot.*` recovery signals through the SAME per-tenant sink the engine uses
   * for `offline.*` / `sync.*`, without reaching into the private field.
   */
  track(event: string, props?: Record<string, unknown>): void {
    this.emitTelemetry(event, props)
  }

  /**
   * Single safe chokepoint for every telemetry emit. Telemetry is diagnostic —
   * a throwing sink (a PostHog/capture bug, a serialization error on the props)
   * must NEVER turn a completed sync outcome into a failure. Without this, a
   * throw from `track(...)` placed on a SUCCESS path would reject the enclosing
   * op: a `boot.time_to_online` throw would abort the post-readiness eviction +
   * backfill kicks, a `boot.resource_pull` throw would mark a fully-pulled
   * resource `error`, and an `on_demand_fetch` throw would surface a hydrated
   * slice as an `offlineMiss`. Swallow + log instead (§2.1.8: the loss IS
   * surfaced — console.error — it just never corrupts the sync result).
   */
  private emitTelemetry(event: string, props?: Record<string, unknown>): void {
    try {
      this.telemetry?.track(event, props)
    } catch (err) {
      console.error(`[SyncEngine] telemetry sink threw for "${event}":`, err)
    }
  }

  async start(): Promise<void> {
    this.startInFlight = true
    try {
      await this.runStart()
    } finally {
      this.startInFlight = false
    }
  }

  /** Expose the HTTP client for custom API calls (e.g. cancel execution). */
  get http(): HttpClientAdapter {
    return this.httpClient
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerResource(config: OfflineResourceConfig<any>): void {
    this.resources.set(config.name, config)
  }

  /**
   * Block until `getAuthToken()` returns a non-empty token, or the bounded
   * timeout elapses (see {@link AUTH_TOKEN_WAIT_TIMEOUT_MS}). Returns
   * immediately when no token resolver is wired (tests / SSR) or when the token
   * is already present — the common case, at zero cost. On timeout we proceed
   * anyway (never hang the boot); the ensuing pulls fall back to today's 401
   * behaviour and the SSE replay still heals the data. Bails early if a
   * concurrent `stop()`/restart bumped the generation out from under us.
   */
  private async waitForAuthToken(generation: number): Promise<void> {
    const getAuthToken = this.getAuthToken
    if (!getAuthToken) return
    if (getAuthToken()) return

    const startedAt = Date.now()
    while (Date.now() - startedAt < AUTH_TOKEN_WAIT_TIMEOUT_MS) {
      await new Promise<void>((resolve) =>
        setTimeout(resolve, AUTH_TOKEN_POLL_INTERVAL_MS)
      )
      if (generation !== this.startGeneration) return
      if (getAuthToken()) {
        this.track("boot.coldstart.token_waited", {
          waitedMs: Date.now() - startedAt,
        })
        return
      }
    }
    this.track("boot.coldstart.token_wait_timeout", {
      waitedMs: Date.now() - startedAt,
    })
  }

  private async runStart(): Promise<void> {
    this.running = true
    this._status = "syncing"
    const generation = ++this.startGeneration
    // Readiness timer for `boot.time_to_online` (STK-1924): the explicit
    // start()→online duration, emitted on EVERY boot regardless of whether it
    // stalled. Today readiness is only inferable from `boot.stalled`, which
    // fires on the slow tenants alone — so the fast majority is invisible and
    // there is no per-tenant baseline to measure the re-stamp against. Anchored
    // here (not at the withColdStartLock body) so a multi-tab lock wait counts
    // toward the user-perceived wait. `coldStarted` distinguishes a genuine
    // full-pull boot (fresh device / post-wipe) from a warm re-kick where every
    // resource was already cursored and readiness is near-instant.
    const startedAt = Date.now()
    let coldStarted = false

    // Watch for the storage closing out from under us (cross-tab logout/delete
    // closes this tab's Dexie connection — see `recoverFromUnexpectedClose`).
    // Registered once; `start` can run again during recovery without stacking
    // watchers.
    if (!this.closeUnsub && this.storage.onClose) {
      this.closeUnsub = this.storage.onClose(() => {
        void this.recoverFromUnexpectedClose()
      })
    }

    // Gate the whole cold start on the auth token being present (STK-1928).
    // The first wave fires one full-table REST pull per resource; letting them
    // go out before the bearer lands means ~30 requests bounce 401 in a burst
    // right before the heavy pull, only for the data to re-arrive over the SSE
    // replay handshake. Wait here (a no-op when the token is already there) so
    // the pulls carry `Authorization` on the first try. Runs BEFORE
    // `resumePendingScopeInvalidation` (which can also refetch during a wipe)
    // and OUTSIDE the cross-tab cold-start lock (the wait is per-tab).
    await this.waitForAuthToken(generation)
    if (generation !== this.startGeneration) return

    // Finish an interrupted scope invalidation FIRST (see ScopeMetaRow):
    // the wipe must land before cursors hydrate and before any row is
    // served, so a reload mid-invalidation resumes instead of restarting.
    await this.resumePendingScopeInvalidation()

    // Bootstrap the cursor cache from Dexie BEFORE anything else — both the
    // critical-wave cold-start (to skip cursored resources) and the SSE
    // handshake (to build its `?cursor=...` query string) read from it.
    await this.hydrateCursorCache()

    // Cold-start fetches the READINESS set (REFERENCE + HOT) in priority waves:
    // critical → warm → background-priority. A resource only enters its wave if
    // it has no cursor yet — resources that already synced once are caught up by
    // the SSE replay handshake instead. Errors in one resource never block
    // siblings.
    //
    // `tier: "background"` resources are DEFERRED here (STK-1324): they are NOT
    // REST-cold-started, so they don't gate readiness. They still land fully
    // offline + SSE-subscribed — the handshake below emits `cursor=R:0` for
    // them, and the server's replay streams their whole table over SSE
    // (`findChangedSince(0)`, paginated) AFTER `online`, non-blocking and
    // resumable (the cursor advances per replay batch, so a tab killed
    // mid-hydration picks up where it left off). See §snapshotCursors +
    // apps/api/src/sse/replay.ts.
    //
    // SSE is deferred until every READINESS-set wave finishes its REST
    // cold-start so those resources carry a real cursor into the handshake —
    // opening SSE before their cold-start would make the gap detector treat
    // each live event as a missed delta and trigger a `since=0` resync storm.
    // (Deferred background resources are safe at `cursor=R:0` — a full replay,
    // not a gap.)
    // Serialize the cold-start pull across tabs (see `tab-leadership.ts`): with
    // several tabs open, letting each fire its own full-table cold-start at
    // once floods the V2 API and piles concurrent writes onto the shared DB —
    // a prime cause of a wedged multi-tab boot. Holding one cross-tab lock lets
    // the first tab pull while the others wait; a waiting tab's boot screen
    // still clears off the leader's writes (Dexie live-queries are cross-tab),
    // and by the time it acquires the lock its cursors exist so its own
    // cold-start is a near-no-op. Degrades to today's per-tab behaviour where
    // Web Locks are unavailable, or if a hung leader can't be waited out.
    await withColdStartLock(async ({ waitedMs, locked }) => {
      if (locked && waitedMs > 250) {
        this.track("boot.coldstart.lock_waited", { waitedMs })
      } else if (!locked && waitedMs > 0) {
        // Couldn't acquire within the timeout — a leader is likely hung. We ran
        // unlocked to avoid wedging this tab; worth surfacing.
        this.track("boot.coldstart.lock_timeout", { waitedMs })
      }

      // Re-hydrate cursors AFTER winning the lock: the initial hydrate above ran
      // before we queued behind the leader, so its cache is empty for a waiting
      // tab. By now the leader has written `_syncMeta`, so refreshing here lets
      // both the wave skip-check and — crucially — the SSE handshake below send
      // real `?cursor=...` values. Without this the queued tab would open SSE
      // at cursor 0 and trigger a full `since=0` replay storm, re-creating the
      // very multi-tab write contention this lock exists to prevent. Cheap and
      // idempotent when uncontended (cache is already warm).
      await this.hydrateCursorCache()

      for (const wave of PRIORITY_WAVES) {
        if (generation !== this.startGeneration) return

        const coldStartInWave = (await this.coldStartResources(wave)).filter(
          (config) => config.tier !== "background"
        )
        if (coldStartInWave.length === 0) continue
        coldStarted = true

        await Promise.all(
          coldStartInWave.map((config) =>
            this.syncResource(config, generation).catch((err) => {
              console.error(
                `[SyncEngine] Failed to sync resource "${config.name}":`,
                err
              )
            })
          )
        )
      }
    })

    if (generation !== this.startGeneration) return

    // The readiness set (REFERENCE + HOT) has finished its REST cold-start.
    // Open SSE; the handshake sends every registered resource's cursor — real
    // values for the readiness set, `0` for the deferred background tier — and
    // the server replays deltas (readiness) + full tables (background) over the
    // stream. The app is usable NOW; background history streams in behind it.
    this.connectTransport()
    this._status = "online"

    // Explicit readiness signal (STK-1924). Emitted for EVERY boot — the
    // always-on generalization of the stall-only `boot.*` events, so a tenant
    // whose boot never stalls still contributes a time-to-online baseline. Low
    // cardinality: two numeric/boolean props, per-tenant/user via the sink's
    // super-properties.
    this.emitTelemetry("boot.time_to_online", {
      durationMs: Date.now() - startedAt,
      coldStart: coldStarted,
    })

    // HOT-tier reclaim + floor refresh. Non-blocking: readiness is NOT gated
    // on eviction. Existing users (full pre-windowing tables) shrink here; new
    // users are already windowed from cold-start. Then keep the floor fresh
    // across day-rolls while the tab lives.
    void this.runHotEvictionPass(generation).catch(
      this.onBackgroundError("Hot eviction pass failed")
    )
    this.ensureDayRollWatcher()

    // Heavy-column backfill (STK-1325): resources synced metadata-light on the
    // list path (emails) backfill their body column here, resumably, behind the
    // usable app. Non-blocking — readiness is NOT gated on it.
    void this.runBodyBackfillAll(generation).catch(
      this.onBackgroundError("Body backfill failed")
    )
  }

  /**
   * Run the resumable body-backfill for every `backfillBody` resource, one at a
   * time (low-priority, never competes with the live app). Per-resource
   * failures are isolated.
   *
   * Single-flighted: a pass that dies on a transient error (a network blip
   * after readiness) clears the flag in `finally`, so the `onReconnect` re-kick
   * resumes it from the persisted `bodyCursor`. A pass that's still streaming
   * makes the re-kick a no-op, so reconnect never spawns a duplicate loop.
   */
  private bodyBackfillInFlight = false

  private async runBodyBackfillAll(generation: number): Promise<void> {
    if (this.bodyBackfillInFlight) return
    this.bodyBackfillInFlight = true
    try {
      for (const config of this.resources.values()) {
        if (!config.backfillBody) continue
        if (!this.isCurrent(generation) || !this.running) return
        try {
          await this.runBodyBackfill(config, generation)
        } catch (err) {
          console.error(
            `[SyncEngine] body backfill failed for "${config.name}":`,
            err
          )
        }
      }
    } finally {
      this.bodyBackfillInFlight = false
    }
  }

  /**
   * Resumable heavy-column backfill for one resource: page through
   * `GET {full}?body=true&since=<bodyCursor>` and MERGE each body into the
   * metadata row already in the store (never replacing it). Advances
   * `bodyCursor` per page (resumable); never touches `lastSyncId`. Skips
   * change-queue-active rows (the body backfills once the edit settles).
   */
  private async runBodyBackfill(
    config: OfflineResourceConfig,
    generation: number
  ): Promise<void> {
    const pageSize = config.bodyBackfillPageSize ?? 50
    const shield = await this.changeQueue.getActiveEntityIds(config.name)
    let cursor = Number((await this.getSyncMeta(config.name))?.bodyCursor ?? 0)

    while (true) {
      if (!this.isCurrent(generation) || !this.running) return

      const response = await this.httpClient.get<
        SyncPullResponse<{ id: string }>
      >(config.endpoints.full, {
        body: "true",
        since: cursor.toString(),
        limit: pageSize.toString(),
      })

      if (!this.isCurrent(generation) || !this.running) return

      const bodies = response.operations
        .filter((op) => op.op === "upsert")
        .map((op) => op.data as { id: string })

      for (const body of bodies) {
        if (shield.has(body.id)) continue
        const existing = await this.storage.get<{ id: string }>(
          config.tableName,
          body.id
        )
        // Merge into the metadata row; skip rows not present locally (never
        // cold-started / evicted) — the backfill only fills existing rows.
        if (existing) {
          await this.storage.put(config.tableName, { ...existing, ...body })
        }
      }

      cursor = Number(response.latestSyncId)
      await this.saveBodyCursor(config.name, cursor)

      if (!response.hasMore || bodies.length < pageSize) break
    }
  }

  /**
   * Open-before-backfill email BODY fetch (STK-1325) — hydrate one email's body
   * on demand via `GET /emails/:id`, merged into its metadata row. Returns
   * `{ offlineMiss:true }` with no writes when offline / on failure.
   */
  async fetchEmailBody(emailId: string): Promise<FetchEmailBodyResult> {
    return runFetchEmailBody(emailId, this.buildOnDemandEmailDeps())
  }

  private buildOnDemandEmailDeps(): OnDemandEmailDeps {
    return {
      storage: this.storage,
      httpClient: this.httpClient,
      getActiveEntityIds: (resource) =>
        this.changeQueue.getActiveEntityIds(resource),
      isOnline: () =>
        typeof navigator === "undefined" ? true : navigator.onLine,
    }
  }

  /** Build the eviction module's dependency bundle from engine internals. */
  private buildEvictionDeps(): HotEvictionDeps {
    return {
      storage: this.storage,
      httpClient: this.httpClient,
      getActiveEntityIds: (resource) =>
        this.changeQueue.getActiveEntityIds(resource),
      getSyncMeta: (resource) => this.getSyncMeta(resource),
      saveSyncMeta: (resource, meta) => this.saveSyncMeta(resource, meta),
    }
  }

  /**
   * Below-floor ON-DEMAND fetch (STK-1323) — hydrate a past-date window
   * (older than the kept floor) into the local tables. Returns
   * `{ offlineMiss:true }` with no side effects when offline / on failure.
   * Never touches `_syncMeta` (these rows are ephemeral, below the floor).
   */
  async fetchOnDemandWindow(window: OnDemandWindow): Promise<OnDemandResult> {
    const startedAt = Date.now()
    const result = await runOnDemandWindow(window, this.buildOnDemandDeps())
    // Below-window on-demand visibility (STK-1924): today this endpoint is
    // absent from PostHog and only sampled in Sentry, so how often — and how
    // far below the kept floor — the recap reaches for older orders is unknown.
    // `depthDays` = how far back the OLDEST bound reaches from today;
    // `spanDays` = the width of the requested slice. Both derived on the
    // client, low-cardinality numerics.
    const { depthDays, spanDays } = onDemandWindowSpan(window, startedAt)
    this.emitTelemetry("preorders.on_demand_fetch", {
      axis: window.axis,
      depthDays,
      spanDays,
      hydrated: result.hydrated,
      offlineMiss: result.offlineMiss,
      durationMs: Date.now() - startedAt,
    })
    return result
  }

  private buildOnDemandDeps(): OnDemandDeps {
    return {
      storage: this.storage,
      httpClient: this.httpClient,
      getActiveEntityIds: (resource) =>
        this.changeQueue.getActiveEntityIds(resource),
      isOnline: () =>
        typeof navigator === "undefined" ? true : navigator.onLine,
    }
  }

  /**
   * Single-PO ON-DEMAND fetch by id (STK-1972) — hydrate one out-of-window
   * pre-order (+ its flows) into the local tables so a deep-linked `/recap/:id`
   * resolves. `{ offlineMiss:true }` when offline / on failure; `{ found:false }`
   * when the server has no visible row. Ephemeral — never touches `_syncMeta`.
   */
  async fetchPreOrderById(id: string): Promise<FetchPreOrderByIdResult> {
    const startedAt = Date.now()
    const result = await runFetchPreOrderById(id, this.buildOnDemandDeps())
    this.emitTelemetry("preorders.on_demand_by_id_fetch", {
      found: result.found,
      offlineMiss: result.offlineMiss,
      durationMs: Date.now() - startedAt,
    })
    return result
  }

  /** The `windowStart` shared by the hot resources this pass will reconcile. */
  private representativeWindowStart(): string | undefined {
    for (const config of this.resources.values()) {
      const windowStart = this.windowStartFor(config)
      if (windowStart !== undefined) return windowStart
    }
    return undefined
  }

  /** Refresh every hot resource's floor + reconcile the local store to it. */
  private async runHotEvictionPass(generation: number): Promise<void> {
    if (!this.isCurrent(generation)) return
    await runHotEviction(
      this.resources.values(),
      (config) => this.windowStartFor(config),
      this.buildEvictionDeps()
    )
    // Record the window this pass reconciled so a same-day visibility regain
    // is a no-op (avoids redundant floor fetches).
    this.lastEvictionWindowStart = this.representativeWindowStart() ?? null
  }

  /**
   * Register a day-roll watcher (once per engine instance). On tab re-focus we
   * re-run eviction only when the calendar day advanced — the window slid, the
   * floor rose, and rows now outside the window must be evicted. Guarded for
   * non-browser environments (SSR / tests without a DOM).
   */
  private ensureDayRollWatcher(): void {
    if (this.dayRollWatcherRegistered) return
    if (typeof document === "undefined") return
    this.dayRollWatcherRegistered = true
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return
      // The listener outlives a `stop()` (registered once per instance, never
      // removed). Don't kick off eviction I/O against a torn-down / logged-out
      // engine whose DB may be mid-wipe — `runHotEvictionPass` is called with
      // the LIVE `startGeneration` here, so its `isCurrent` guard can't catch a
      // post-stop refocus the way the cold-start caller's captured generation
      // does.
      if (!this.running) return
      const current = this.representativeWindowStart()
      if (current === undefined || current === this.lastEvictionWindowStart) {
        return
      }
      void this.runHotEvictionPass(this.startGeneration).catch(
        this.onBackgroundError("Day-roll eviction pass failed")
      )
    })
  }

  /** Resources in the given priority tier that have no cursor yet. */
  private async coldStartResources(
    priority: ResourcePriority
  ): Promise<OfflineResourceConfig[]> {
    const tier = this.resourcesByPriority(priority)
    const out: OfflineResourceConfig[] = []
    for (const config of tier) {
      const meta = await this.getSyncMeta(config.name)
      // BIGSERIAL syncIds round-trip through JSON as strings (pg/drizzle
      // stringify BIGINT to preserve precision), so a saved `lastSyncId=0`
      // is the *string* "0" — strict `=== 0` would always be false and the
      // second navigation would skip cold-start for any resource whose
      // first pull found nothing. When the SSE delivery loses the race
      // against a tab tear-down, the resource then never recovers. Match
      // `hydrateCursorCache`'s coercion.
      if (!meta || Number(meta.lastSyncId) === 0) out.push(config)
    }
    return out
  }

  /** Read every persisted `_syncMeta` row into `cursorCache`. */
  private async hydrateCursorCache(): Promise<void> {
    const cache: Record<string, number> = {}
    for (const config of this.resources.values()) {
      const meta = await this.getSyncMeta(config.name)
      // BIGSERIAL syncIds round-trip through JSON as strings (pg/drizzle
      // stringify BIGINT to preserve precision). Coerce here so the SSE
      // transport's `Number.isFinite` cursor filter doesn't drop them.
      const n = Number(meta?.lastSyncId)
      if (Number.isFinite(n) && n > 0) cache[config.name] = n
    }
    this.cursorCache = cache
  }

  private resourcesByPriority(
    priority: ResourcePriority
  ): OfflineResourceConfig[] {
    const out: OfflineResourceConfig[] = []
    for (const config of this.resources.values()) {
      const tier = config.priority ?? "background"
      if (tier === priority) out.push(config)
    }
    return out
  }

  stop(): void {
    this.running = false
    this.startGeneration++ // Invalidate any in-flight start()
    // Disarm a coalescing scope-invalidation timer: if it fired against a
    // stopped engine the run would bail anyway (running guard), and the next
    // start()'s handshake re-compares versions. An in-flight RUN is left
    // alone — it manages its own stop/start cycle.
    if (this.scopeInvalidationTimer) {
      clearTimeout(this.scopeInvalidationTimer)
      this.scopeInvalidationTimer = null
    }
    if (this.closeUnsub) {
      this.closeUnsub()
      this.closeUnsub = null
    }
    if (this.transportHandler) {
      this.transport.offEvent(this.transportHandler)
      this.transportHandler = null
    }
    this.transport.disconnect()
    this._status = "idle"
  }

  /**
   * Recover from an unexpected storage close. The canonical trigger: a second
   * tab logs out, `resetOfflineStorage` calls `offlineDb.delete()`, and the
   * resulting versionchange closes THIS tab's Dexie connection. While closed,
   * reactive reads return empty (UI shows no prices/data) and writes throw
   * `DatabaseClosedError`, yet the SSE connection stays healthy on its own
   * heartbeat so nothing auto-reconnects — the gap persists until a manual
   * reload. We reopen in place and restart the engine (cold-start repopulates
   * an emptied DB; the SSE replay handshake catches up a non-destructive close).
   *
   * A close while the engine is stopped is OUR OWN logout path (reset calls
   * `stop()` before `delete()`); we never fight it, and never reopen a database
   * mid-deletion (that would mask the wipe — see `reset.ts`).
   */
  private async recoverFromUnexpectedClose(): Promise<void> {
    if (!this.running || this.recovering) return
    this.recovering = true
    this._status = "syncing"
    this.telemetry?.track("offline.db_closed", { recovering: true })
    try {
      await this.storage.reopen?.()
      // A deliberate stop() (logout/reset) can land while reopen() was
      // awaiting. Don't restart the engine the user just tore down — bail if
      // we're no longer running.
      if (!this.running) return
      this.telemetry?.track("offline.db_reopened")
      this.stop()
      await this.start()
    } catch (err) {
      this._status = "error"
      this.telemetry?.track("offline.db_reopen_failed", {
        error: err instanceof Error ? err.message : String(err),
      })
      console.error(
        "[SyncEngine] Failed to recover after unexpected storage close:",
        err
      )
    } finally {
      this.recovering = false
    }
  }

  async pushChanges(resource?: string): Promise<void> {
    // Single-flight, keyed per drain scope:
    //   - resource=undefined → "*" (global drain, processes every resource)
    //   - resource="A" → "A" (only A's queue)
    //
    // Previously a single shared promise meant a per-resource drain for
    // "A" would return the in-flight promise for an unrelated "B" drain,
    // silently skipping A's queue until the next tick. Per-scope keys
    // let independent resources drain concurrently while still
    // coalescing repeated calls for the SAME scope. The global "*"
    // drain naturally covers per-resource scopes too: if a global drain
    // is running, a per-resource call still gets its own (lightweight)
    // drain because their keys differ — but `dequeueNext(resource)`
    // races on the queue safely (transactional dequeue prevents
    // double-pop), so the worst case is a no-op when the global drain
    // already emptied the queue.
    const key = resource ?? "*"
    const inflight = this.pushInFlight.get(key)
    if (inflight) return inflight

    const drain = this.drainPush(resource).finally(() => {
      this.pushInFlight.delete(key)
    })
    this.pushInFlight.set(key, drain)
    return drain
  }

  /**
   * The actual push-drain loop. Extracted from `pushChanges` so the
   * single-flight wrapper can hold a reference to the in-flight
   * promise without changing the public API.
   *
   * Note: `resource` is captured by the FIRST caller. A second caller
   * scoping to a different resource still gets the first caller's
   * drain back — that's acceptable: the global (`undefined`) drain
   * processes every resource, and per-resource drains are best-effort
   * (the SSE/reconnect path re-fires them anyway).
   */
  private async drainPush(resource?: string): Promise<void> {
    let entry = await this.changeQueue.dequeueNext(resource)

    // Deferred piggyback resync: firing a resync MID-LOOP is dangerous — the
    // background delta GET returns before later changes in this drain have been
    // pushed, so `applyOperations` can overwrite optimistic Dexie writes with
    // stale server state (the shield is already lifted because `markSynced`
    // deleted the queue entry). Collect candidates and fire AFTER the loop so
    // the delta fetch sees every change this drain committed.
    const piggybackCandidates = new Map<
      string,
      { config: OfflineResourceConfig; latestSyncId: number }
    >()

    while (entry) {
      const config = this.resources.get(entry.resource)
      if (!config) {
        await this.changeQueue.markSynced(entry.id)
        entry = await this.changeQueue.dequeueNext(resource)
        continue
      }

      try {
        const response = await this.postWithRetry<SyncPushResponse<unknown>>(
          config.endpoints.push,
          { changes: [entry.change], clientSyncId: 0 }
        )
        await this.changeQueue.markSynced(entry.id)

        // Shield lifted for this change — recover any server op the shield
        // dropped while it was in flight (no-op unless a drop was recorded).
        void this.recoverShieldedDrops(config).catch((err) => {
          console.error(
            `[SyncEngine] Shielded-drop recovery failed for "${config.name}":`,
            err
          )
        })

        // Track the highest latestSyncId per resource for deferred piggyback.
        if (response.latestSyncId) {
          const prev = piggybackCandidates.get(config.name)
          if (!prev || response.latestSyncId > prev.latestSyncId) {
            piggybackCandidates.set(config.name, {
              config,
              latestSyncId: response.latestSyncId,
            })
          }
        }
      } catch {
        await this.changeQueue.markFailed(entry.id)
        // The optimistic write was rejected; any server op the shield dropped
        // while this change was in flight must still be recovered. markFailed
        // lifts the shield (failed entries aren't "active"), so reconcile to
        // authoritative state from the recorded floor.
        void this.recoverShieldedDrops(config).catch((err) => {
          console.error(
            `[SyncEngine] Shielded-drop recovery failed for "${config.name}":`,
            err
          )
        })
        break // Stop pushing on failure, will retry later
      }

      entry = await this.changeQueue.dequeueNext(resource)
    }

    // Piggyback safety net (deferred): server returns user-visible
    // latestSyncId with each push response. If it's ahead of our local
    // cursor, we missed an SSE event — trigger a surgical resync. Firing
    // here (after the drain) guarantees the delta fetch sees every change
    // this drain committed, preventing stale overwrites of later changes.
    for (const [name, { config, latestSyncId }] of piggybackCandidates) {
      const localCursor = this.cursorCache[name] ?? 0
      if (latestSyncId > localCursor && !this.resyncing.has(name)) {
        void this.resyncResource(config).catch((err) => {
          console.error(
            `[SyncEngine] Push-piggyback resync failed for "${name}":`,
            err
          )
        })
      }
    }
  }

  private isCurrent(generation: number): boolean {
    return generation === this.startGeneration
  }

  /**
   * Error sink for fire-and-forget background tasks (eviction, body backfill,
   * reconnect drain). A `DatabaseClosedError` is EXPECTED here — a cross-tab
   * logout/delete can close the store mid-task, and the `onClose` watcher
   * reopens + restarts anyway — so swallow it rather than let it surface as an
   * unhandled promise rejection (Sentry `NEXTJS-FRONTEND-2K`). Anything else is
   * a real fault and is logged.
   */
  private onBackgroundError(context: string): (err: unknown) => void {
    return (err) => {
      if (isDbClosedError(err)) return
      console.error(`[SyncEngine] ${context}:`, err)
    }
  }

  private async syncResource(
    config: OfflineResourceConfig,
    generation: number
  ): Promise<void> {
    // Cold-start path only — resources with an existing cursor are caught up
    // by the SSE replay handshake (see `connectTransport`). Mark in-flight
    // before the network round-trip so reactive subscribers (useSyncStatus)
    // can show skeletons during the first pull rather than after the first
    // paginated response lands.
    const meta = await this.getSyncMeta(config.name)
    await this.setResourceStatus(config.name, "syncing", meta)

    try {
      await this.fullFetchWithRetry(config, generation)
    } catch (err) {
      if (this.isCurrent(generation)) {
        await this.setResourceStatus(config.name, "error")
        // Even on failure, register the resource in the cursor cache so
        // the SSE handshake still sends `cursor=resource:0` for it. The
        // server uses the handshake cursor list as the connection's
        // live-event subscription set — without this fallback, a transient
        // cold-start failure (network blip, server restart) would leave
        // the resource permanently absent from the handshake and deaf to
        // live events until the next page reload. The server's replay
        // handler will then re-pull from `since=0` and heal the gap.
        if (this.cursorCache[config.name] === undefined) {
          this.cursorCache[config.name] = 0
        }
      }
      throw err
    }
  }

  /**
   * Retry `fullFetch` on transient network failures. Chrome's network stack
   * occasionally reconfigures mid-request (especially on the first parallel
   * burst of a cold worker) and aborts in-flight `fetch()` calls with
   * `net::ERR_NETWORK_CHANGED` / `TypeError: Failed to fetch`. Without a
   * retry, the resource ends up with `lastFullFetchAt: null` even after a
   * later SSE event flips its status to `online` — the workspace boot
   * screen stays stuck on N/14 essentials forever.
   *
   * Three attempts, ~500ms backoff (500ms, 1000ms) — keeps the cold-start
   * budget bounded while absorbing the documented transient class. Server
   * 4xx/5xx are NOT retried (caller's bug should surface, not be hidden).
   */
  private async fullFetchWithRetry(
    config: OfflineResourceConfig,
    generation: number
  ): Promise<void> {
    let lastErr: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      if (!this.isCurrent(generation)) return
      try {
        await this.fullFetch(config, generation)
        return
      } catch (err) {
        if (!isTransientFetchError(err)) throw err
        lastErr = err
        if (attempt < 2) {
          await new Promise<void>((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1))
          )
        }
      }
    }
    throw lastErr
  }

  /**
   * Retry an HTTP POST on transient network failures. Same shape as
   * `fullFetchWithRetry`, used by the change-queue drain in `pushChanges`.
   * The offline→online transition is the worst window for this: Chrome's
   * network stack reconfigures right when the SSE `__reconnect` fires
   * `pushChanges`, and the in-flight POST gets aborted with
   * `net::ERR_NETWORK_CHANGED`. Without a retry, the queue entry is marked
   * "failed", the loop breaks, and the message stays unpushed until the
   * user performs another mutation. Visible in run 25955973106 (P1-CO-1's
   * fresh-context probe showed the server never received the offline-sent
   * message).
   */
  private async postWithRetry<T>(url: string, body: unknown): Promise<T> {
    let lastErr: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await this.httpClient.post<T>(url, body)
      } catch (err) {
        if (!isTransientFetchError(err)) throw err
        lastErr = err
        if (attempt < 2) {
          await new Promise<void>((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1))
          )
        }
      }
    }
    throw lastErr
  }

  private async setResourceStatus(
    resource: string,
    status: SyncStatus,
    known?: SyncMeta | null
  ): Promise<void> {
    const existing = known ?? (await this.getSyncMeta(resource))
    await this.saveSyncMeta(resource, {
      resource,
      lastSyncId: existing?.lastSyncId ?? 0,
      lastFullFetchAt: existing?.lastFullFetchAt ?? null,
      status,
      // Preserve the windowed low watermark across status-only writes.
      windowFloor: existing?.windowFloor,
      bodyCursor: existing?.bodyCursor,
    })
  }

  /**
   * Absolute `windowStart` (YYYY-MM-DD, UTC) for a `hot` resource — `today −
   * window.days`. Open-ended into the future (no `windowEnd`), so all upcoming
   * deliveries stay HOT. `undefined` for reference/background resources, which
   * pull the whole table unchanged. Computed from `Date.now()` (fake-timer
   * controllable in the DSL tests); the client owns the absolute date so a
   * paginated pull never shifts at a midnight day-roll.
   */
  private windowStartFor(config: OfflineResourceConfig): string | undefined {
    if (config.tier !== "hot") return undefined
    const days = config.window?.days ?? 30
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10)
  }

  private async fullFetch(
    config: OfflineResourceConfig,
    generation: number
  ): Promise<void> {
    let offset = 0
    // Per-resource override for heavy payloads (e.g. emails ~10 KB/row); keeps
    // each server-side stringify short so the V2 API event loop yields between
    // pages. Defaults to 1000. See OfflineResourceConfig.pageSize.
    const pageSize = config.pageSize ?? 1000
    let latestSyncId = 0
    // Per-resource cold-start pull accounting (`boot.resource_pull`, STK-1924):
    // the cold-start equivalent of `sync.resync.*`. `rows` is the load driver
    // the re-stamp targets (flows are THE bottleneck); `durationMs` is the wall
    // time. `bytes` is an approximate uncompressed payload size (JSON string
    // length, UTF-16 units ≈ bytes for this mostly-ASCII JSON) — computed only
    // when a telemetry sink is wired so tests/SSR never pay the re-serialize.
    const pullStartedAt = Date.now()
    let rows = 0
    let bytes = 0

    // HOT resources send an absolute `windowStart`; the server returns rows
    // `syncId >= windowFloor` (newest-first) + the floor in the envelope.
    // Reference/background send no window → whole-table pull, unchanged.
    const windowStart = this.windowStartFor(config)
    let windowFloor: number | undefined

    // Paginate through all data via offset on the full endpoint.
    while (true) {
      if (!this.isCurrent(generation)) return

      const params: Record<string, string> = {
        limit: pageSize.toString(),
        offset: offset.toString(),
      }
      if (windowStart !== undefined) params.windowStart = windowStart

      // The `& { windowFloor? }` intersection reads the windowed envelope
      // field locally (the shared SyncPullResponse gains it via STK-1321).
      const response = await this.httpClient.get<
        SyncPullResponse<{ id: string }> & { windowFloor?: number }
      >(config.endpoints.full, params)

      if (!this.isCurrent(generation)) return

      const items = response.operations
        .filter((op) => op.op === "upsert")
        .map((op) => op.data)

      if (items.length > 0) {
        await this.storage.putMany(config.tableName, items)
      }
      rows += items.length
      if (this.telemetry) bytes += JSON.stringify(response.operations).length

      // BIGSERIAL sync_ids round-trip through JSON as strings (postgres-js
      // stringifies BIGINT to preserve precision past 2^53). Coerce at the
      // ingestion boundary so downstream Number.isFinite() / arithmetic
      // checks (e.g. the SSE handshake URL filter, gap detector) see
      // actual numbers, not numeric strings that fail strict number checks.
      latestSyncId = Number(response.latestSyncId)
      // Floor is deterministic per (windowStart, data), so every page returns
      // the same value — last write wins, identical either way.
      if (response.windowFloor !== undefined) {
        windowFloor = Number(response.windowFloor)
      }

      if (!response.hasMore || items.length < pageSize) break
      offset += pageSize
    }

    if (!this.isCurrent(generation)) return

    // `lastSyncId` = the GLOBAL latestSyncId (delta cursor, UNCHANGED by
    // windowing); `windowFloor` = the low materialization watermark.
    await this.saveSyncMeta(config.name, {
      resource: config.name,
      lastSyncId: latestSyncId,
      lastFullFetchAt: Date.now(),
      status: "online",
      windowFloor,
    })
    this.cursorCache[config.name] = latestSyncId

    // Reached only on a completed pull (generation-cancelled pulls `return`
    // above and never emit). `windowed` splits floor-bounded HOT resources
    // (pre-orders/flows) from whole-table reference pulls.
    this.emitTelemetry("boot.resource_pull", {
      resource: config.name,
      rows,
      bytes,
      durationMs: Date.now() - pullStartedAt,
      windowed: windowStart !== undefined,
    })
  }

  private async applyOperations(
    config: OfflineResourceConfig,
    operations: SyncOperation<{ id: string }>[]
  ): Promise<void> {
    // Offline-first shield: never let an incoming sync echo overwrite a row that
    // still has an IN-FLIGHT local change. The user's optimistic write wins
    // until it is acknowledged. Without this, a late echo of an OLDER state —
    // e.g. a `delete` arriving just after the user restored the row — clobbers
    // the optimistic write and the row visibly flaps (deleted → restored →
    // deleted). The canonical server row re-applies once the change acks:
    // `markSynced` clears the queue entry (lifting the shield) and the
    // push-piggyback resync re-fetches the row.
    const shielded = await this.changeQueue.getActiveEntityIds(config.name)
    // …plus rows shielded transiently by change-queue-bypassing writes
    // (pushBatch's optimistic apply) for the duration of their POST.
    const transient = this.transientShield.get(config.name)
    const isShielded = (id: string): boolean =>
      shielded.has(id) || (transient?.has(id) ?? false)

    // Batch upserts for performance — individual puts block the main thread
    const upserts: { id: string }[] = []
    const deletes: string[] = []
    // Evict deletes (STK-1238): the row left THIS viewer's scope. Deleted
    // like a normal delete (hard-remove — an evict is not a tombstone, even
    // on softDelete resources whose deletes arrive as upserts-with-deletedAt)
    // plus the config's `onEvict` child cascade.
    //
    // Known bound: an evict for a SHIELDED row (user editing it while an
    // admin moved its group) is dropped like any other shielded op, and the
    // recovery resync can't re-fetch a row the viewer no longer sees — the
    // stale copy survives until the next scope invalidation or window
    // eviction.
    const evictions: string[] = []

    for (const op of operations) {
      if (isShielded(op.id)) {
        // Dropping the echo is correct for the flap case (a stale echo of our
        // own in-flight write), but a GENUINE concurrent server change would
        // also be dropped — and the live handler has already advanced the
        // cursor past it, so no later resync would re-fetch it. Record the
        // lowest pre-op cursor as a re-fetch floor; `recoverShieldedDrops`
        // resyncs from here once the change settles and the shield lifts,
        // pulling authoritative state (NOT replaying this echo).
        const floorCandidate =
          op.prevSyncId ?? Math.max(0, (op.syncId ?? 1) - 1)
        const current = this.shieldRefetchFloor.get(config.name)
        if (current === undefined || floorCandidate < current) {
          this.shieldRefetchFloor.set(config.name, floorCandidate)
        }
        continue
      }
      if (op.op === "upsert") {
        upserts.push(op.data)
      } else if (op.op === "delete") {
        deletes.push(op.id)
        if (op.evict) evictions.push(op.id)
      }
    }

    if (upserts.length > 0) {
      await this.storage.putMany(config.tableName, upserts)
    }
    for (const id of deletes) {
      await this.storage.delete(config.tableName, id)
    }
    if (config.onEvict) {
      for (const id of evictions) {
        await config.onEvict(id, this.storage)
      }
    }
  }

  private connectTransport(): void {
    this.transportHandler = (event: string, data: unknown) => {
      if (event === "__disconnect") {
        this._status = "offline"
        this.telemetry?.track("sync.disconnect")
        return
      }
      if (event === "__reconnect") {
        this._status = "syncing"
        this.telemetry?.track("sync.reconnect")
        void this.onReconnect().catch(
          this.onBackgroundError("Reconnect handling failed")
        )
        return
      }

      // Scope-version control frames (STK-1238) — handled BEFORE the resource
      // demux (they are not resources). `scope` arrives once per handshake:
      // compare against the persisted version, wipe on mismatch (the device
      // missed a membership change while disconnected). `scope-changed` is the
      // live push: always invalidate.
      if (event === "scope") {
        const v = (data as { v?: unknown } | null)?.v
        if (typeof v === "string") {
          void this.handleScopeHandshake(v).catch(
            this.onBackgroundError("Scope handshake check failed")
          )
        }
        return
      }
      if (event === "scope-changed") {
        const v = (data as { v?: unknown } | null)?.v
        if (typeof v === "string") this.scheduleScopeInvalidation(v)
        return
      }

      // Demux: event name = resource name
      const config = this.resources.get(event)
      if (!config) return

      // Two payload shapes share the same event name:
      //   - Replay batch (handshake): SyncReplayEvent — `replay: true`,
      //     `operations: [...]`, `latestSyncId: N`. One per cursored resource.
      //   - Live op: SyncOperation — single `{op, id, data, syncId}`.
      // We distinguish by the `replay` flag rather than introducing a second
      // event name, so the on-the-wire vocabulary stays per-resource.
      const replay = data as Partial<SyncReplayEvent<{ id: string }>>
      if (
        replay &&
        replay.replay === true &&
        Array.isArray(replay.operations)
      ) {
        const ops = replay.operations as SyncOperation<{ id: string }>[]
        const latest = replay.latestSyncId ?? 0
        // Journal evictions (STK-1584): rows that left this viewer's scope
        // while the device was offline, served by the replay handshake from
        // the server's scope-transition journal. Counted here (not in
        // applyOperations) so the metric isolates the offline-missed case —
        // live evicts have their own delivery path below.
        const evictCount = ops.filter(
          (op) => op.op === "delete" && op.evict
        ).length
        if (evictCount > 0) {
          this.telemetry?.track("sync.journal_evict", {
            resource: config.name,
            count: evictCount,
          })
        }
        this.trackTransportApply(
          this.applyOperations(config, ops)
            .then(() => this.updateSyncMeta(config.name, latest))
            .catch((err) => {
              console.error(
                `[SyncEngine] Failed to apply replay batch for "${config.name}":`,
                err
              )
            })
        )
        return
      }

      const op = data as SyncOperation<{ id: string }>

      // Evict deletes are SELF-CONTAINED and must bypass the resyncing-drop
      // and gap-check machinery below (STK-1238): both recovery paths go
      // through `GET /:resource/changes`, whose visibility-filtered SQL can
      // NEVER redeliver an evict for a row the viewer no longer sees — a
      // dropped evict is lost forever and the stale row survives. The gap
      // check would drop it systematically: the server's per-connection
      // `prevSyncId` anchor REGRESSES when the row leaves visibility (the
      // resolver skips rows the viewer can no longer see), so it rarely
      // matches the local cursor. Applying is always safe — it only removes
      // one row by id — and `updateSyncMeta`'s MAX guard advances the cursor
      // so the next live event anchors cleanly.
      if (op.op === "delete" && op.evict) {
        // Advance cursorCache SYNCHRONOUSLY, mirroring the normal-event
        // path below: within one server flush the events are CHAINED (the
        // follow-up event's `prevSyncId` IS this evict's syncId — see the
        // manager's per-bucket `anchor = evt.syncId` walk), so a same-tick
        // follow-up would otherwise read the pre-evict cursor, see a gap
        // and fire a spurious resync. Same optimistic-advance +
        // rollback-on-failure contract as below; the eventual persisted
        // state is unchanged (`updateSyncMeta` already advanced to this
        // syncId, just asynchronously).
        const evictLocalCursor = this.cursorCache[config.name] ?? 0
        const evictAdvancedTo = Math.max(evictLocalCursor, op.syncId)
        this.cursorCache[config.name] = evictAdvancedTo
        this.trackTransportApply(
          this.applyOperations(config, [op])
            .then(() => this.updateSyncMeta(config.name, op.syncId))
            .catch((err) => {
              // Roll the optimistic advance back (only if no later event
              // moved it on) so gap detection can still surface the missed
              // apply on the next event, exactly like the normal path.
              if (this.cursorCache[config.name] === evictAdvancedTo) {
                this.cursorCache[config.name] = evictLocalCursor
              }
              if (isDbClosedError(err)) return
              console.error(
                `[SyncEngine] Failed to apply evict for "${config.name}":`,
                err
              )
            })
        )
        return
      }

      // Drop live events for a resource currently being resynced — the
      // in-flight `GET /:resource/changes` will return them as part of the
      // catch-up batch, applying them concurrently risks out-of-order writes
      // against the cursor we're about to advance. BUT a row committed
      // server-side AFTER the resync's snapshot is delivered only on this live
      // path and would NOT be in that response — for a terminal row (confirmed
      // order, quiet tenant) no future event ever triggers gap detection, so it
      // stays stale until reload. Record the highest dropped syncId as a
      // watermark; `resyncResource`'s `finally` fires a follow-up resync if the
      // settled cursor never reached it.
      if (this.resyncing.has(config.name)) {
        const existing = this.resyncDropWatermark.get(config.name)
        this.resyncDropWatermark.set(
          config.name,
          Math.max(existing ?? 0, op.syncId)
        )
        return
      }

      // Gap check: every live event tells us what `lastSyncId` the recipient
      // *should* have just before this op (visibility-filtered, computed by
      // the server at flush time). If our local cursor doesn't match, we
      // missed an event — the SSE listener may have silently dropped one,
      // or this is the first event after a partial cold-start. Trigger a
      // surgical `GET /:resource/changes?since=lastSyncId` resync; that
      // response will include the current op + anything we missed.
      const localCursor = this.cursorCache[config.name] ?? 0
      const expected = op.prevSyncId ?? 0
      if (String(expected) !== String(localCursor)) {
        void this.resyncResource(config).catch((err) => {
          console.error(
            `[SyncEngine] Gap-driven resync failed for "${config.name}":`,
            err
          )
        })
        return
      }

      // Advance cursorCache SYNCHRONOUSLY before kicking off the async
      // Dexie write. Two events arriving in the same tick (chat fan-out,
      // proposal approval bulk) would otherwise both observe the
      // pre-event cursor; the second would compute `prevSyncId` against
      // the stale value, see a mismatch, and trigger a spurious resync.
      // updateSyncMeta still does the MAX guard so out-of-order ticks
      // never regress the persisted cursor.
      const advancedTo = Math.max(localCursor, op.syncId)
      this.cursorCache[config.name] = advancedTo

      this.trackTransportApply(
        this.applyOperations(config, [op])
          .then(() => this.updateSyncMeta(config.name, op.syncId))
          .catch((err) => {
            // The synchronous advance above optimistically pulled the
            // cursor forward. If applyOperations failed the row never
            // landed in Dexie — leaving the cursor advanced would silently
            // hide this missed op from gap detection on the next event.
            // Roll back (only if no later event already moved it on) and
            // trigger a resync to recover the failed op.
            if (this.cursorCache[config.name] === advancedTo) {
              this.cursorCache[config.name] = localCursor
            }
            // A closed DB is never fixed by a resync (it writes to the same
            // closed store). It's expected during the logout/reset wipe; if it
            // happened unexpectedly, the `onClose` watcher reopens + restarts.
            // Either way, don't escalate into a futile resync.
            if (isDbClosedError(err)) return
            console.error(
              `[SyncEngine] Failed to apply realtime operation for "${config.name}":`,
              err
            )
            void this.resyncResource(config).catch((resyncErr) => {
              console.error(
                `[SyncEngine] Recovery resync failed for "${config.name}":`,
                resyncErr
              )
            })
          })
      )
    }

    this.transport.onEvent(this.transportHandler)
    const resourceNames = Array.from(this.resources.keys())
    // Hand the transport a token *resolver* rather than baking the bearer into
    // the URL once: the transport re-stamps it on every reconnect, so a token
    // rotated mid-session (password change → prior JWT revoked) is picked up on
    // the next reconnect instead of looping the stream offline until reload.
    const getAuthToken = this.getAuthToken
    this.transport.connect(
      this.sseUrl,
      resourceNames,
      () => this.snapshotCursors(),
      getAuthToken ? () => getAuthToken() : undefined
    )
  }

  private snapshotCursors(): Record<string, number> {
    // Emit a cursor for EVERY registered resource, even ones missing from
    // the cache (cold-start still in flight, failed, or never ran). The
    // server uses the handshake cursor list as the connection's live-event
    // subscription set — omitting a resource here makes the connection
    // permanently deaf to its live events until reconnect. `cursor=R:0`
    // is the explicit "subscribe me, replay from the start" signal; an
    // entry already in cache keeps its real value.
    const cursors: Record<string, number> = {}
    for (const name of this.resources.keys()) {
      // Defensive Number() coercion: BIGSERIAL sync_ids arrive over the
      // wire as JSON strings, and any in-session write path that forgets
      // to coerce (or any future regression) would otherwise produce
      // string entries that fail the `Number.isFinite` filter in
      // sse-transport's buildUrl and silently drop the whole cursor list.
      const raw = this.cursorCache[name] ?? 0
      const n = Number(raw)
      cursors[name] = Number.isFinite(n) ? n : 0
    }
    return cursors
  }

  private async onReconnect(): Promise<void> {
    // SSE replay on the new connection catches the cursors up — we don't
    // need to fan out per-resource delta GETs anymore. Just flush queued
    // mutations once the transport is back.
    await this.pushChanges()
    this._status = "online"

    // Resume a body backfill that died on a transient error (or never finished
    // before the drop). It's resumable via `bodyCursor` and single-flighted, so
    // a still-running pass is a no-op — this only restarts a stalled one.
    void this.runBodyBackfillAll(this.startGeneration).catch(
      this.onBackgroundError("Body backfill failed")
    )
  }

  // -------------------------------------------------------------------------
  // Scope invalidation (STK-1238)
  // -------------------------------------------------------------------------

  /**
   * Handshake-side safety net: compare the server's derived scope version
   * against the persisted one. No stored version (fresh DB, first session) →
   * adopt silently. Mismatch → the viewer's group scope changed while this
   * device was disconnected (missed live event, closed app, second device,
   * out-of-band DB edit) — run the same wipe + cold re-pull as the live path.
   */
  private async handleScopeHandshake(v: string): Promise<void> {
    const stored = await this.storage.get<ScopeMetaRow>(
      SYNC_META_TABLE,
      SCOPE_META_ID
    )
    if (!stored) {
      await this.storage.put(SYNC_META_TABLE, { id: SCOPE_META_ID, v })
      return
    }
    if (stored.v !== v) this.scheduleScopeInvalidation(v)
  }

  /**
   * Coalesced entry point for scope invalidation. Every trigger records the
   * latest version; the first one arms the debounce timer; a trigger landing
   * while a run is in flight is picked up by the run's `finally` reschedule.
   */
  private scheduleScopeInvalidation(v: string): void {
    this.pendingScopeVersion = v
    // Durable marker, fire-and-forget: the debounce (300ms) dwarfs this put,
    // so the marker is persisted long before the run starts. If the page dies
    // anywhere between here and the completed wipe, the next start() resumes.
    void this.persistPendingScopeMarker(v).catch((err) => {
      if (isDbClosedError(err)) return
      console.error("[SyncEngine] Failed to persist scope-pending marker:", err)
    })
    if (this.scopeInvalidationTimer || this.scopeInvalidationRun) return
    this.scopeInvalidationTimer = setTimeout(() => {
      this.scopeInvalidationTimer = null
      this.scopeInvalidationRun = this.runScopeInvalidation()
        .catch((err) => {
          console.error("[SyncEngine] Scope invalidation failed:", err)
        })
        .finally(() => {
          this.scopeInvalidationRun = null
          // A membership change landed mid-run: its rows may have been pulled
          // already (the re-pull ran after the change) but the delta stream
          // will never EVICT what that second change removed — run again.
          if (this.pendingScopeVersion !== null) {
            this.scheduleScopeInvalidation(this.pendingScopeVersion)
          }
        })
    }, SCOPE_INVALIDATION_DEBOUNCE_MS)
  }

  /** Persist the durable invalidation marker (see ScopeMetaRow.pending). */
  private async persistPendingScopeMarker(v: string): Promise<void> {
    const stored = await this.storage.get<ScopeMetaRow>(
      SYNC_META_TABLE,
      SCOPE_META_ID
    )
    if (stored?.pending === v) return
    await this.storage.put(SYNC_META_TABLE, {
      id: SCOPE_META_ID,
      v: stored?.v ?? "",
      pending: v,
    })
  }

  /**
   * Finish an invalidation a previous page never completed. Runs at the top
   * of start(): wipe the group-scoped tables again (idempotent — a partially
   * wiped set just loses less) and adopt the pending version. The cold-start
   * waves that follow re-pull exactly the wiped set, same as step 6 of
   * `runScopeInvalidation`. No settle phase here by design: this engine
   * instance just booted, so it has no in-flight applies of its own, and the
   * previous page's in-flight work died with it.
   */
  private async resumePendingScopeInvalidation(): Promise<void> {
    const stored = await this.storage.get<ScopeMetaRow>(
      SYNC_META_TABLE,
      SCOPE_META_ID
    )
    if (!stored?.pending) return
    this.telemetry?.track("sync.scope_invalidation.resumed")
    await this.wipeGroupScopedResources()
    await this.storage.put(SYNC_META_TABLE, {
      id: SCOPE_META_ID,
      v: stored.pending,
    })
  }

  /** Step 4 of `runScopeInvalidation`, shared with the boot-time resume. */
  private async wipeGroupScopedResources(): Promise<void> {
    for (const config of this.resources.values()) {
      if (!config.groupScoped) continue
      await this.storage.clear(config.tableName)
      await this.storage.delete(SYNC_META_TABLE, config.name)
      delete this.cursorCache[config.name]
      // Queue entries for wiped rows would shield ghost ids against the
      // re-pull and re-push edits on rows that may have left scope.
      await this.changeQueue.clearResource(config.name)
      this.shieldRefetchFloor.delete(config.name)
      this.resyncDropWatermark.delete(config.name)
      this.resyncRequested.delete(config.name)
    }
  }

  /**
   * The viewer's group scope changed: drop every group-scoped table and cold
   * re-pull through the STANDARD start() machinery. Sequence:
   * flush → stop → settle in-flight resyncs → wipe → persist version → start.
   *
   * The wipe deletes each resource's `_syncMeta` row, so `start()` classes it
   * into `coldStartResources` and `fullFetch` re-pulls it with all its
   * existing mechanics (offset pagination, hot `windowStart`, `windowFloor`
   * persistence, body backfill). NO fetch logic lives here by design — the
   * cold-start path is the single owner of re-pull behavior.
   */
  private async runScopeInvalidation(): Promise<void> {
    const v = this.pendingScopeVersion
    this.pendingScopeVersion = null
    if (v === null) return
    // A stopped engine (logout in progress, reset) must not be resurrected;
    // the next start()'s handshake re-compares versions and catches up.
    if (!this.running) return

    this.telemetry?.track("sync.scope_invalidation.started")
    const startedAt = Date.now()

    // 1. Flush pending local edits, hard-capped: an edit against a row
    // leaving scope may 403/404 on push — the queue's failure handling
    // absorbs that; a hung network must not delay dropping the data.
    try {
      await Promise.race([
        this.pushChanges(),
        new Promise<void>((resolve) =>
          setTimeout(resolve, SCOPE_FLUSH_TIMEOUT_MS)
        ),
      ])
    } catch (err) {
      console.error("[SyncEngine] Pre-invalidation flush failed:", err)
    }

    // 2. Tear down transport + invalidate in-flight start() waves. An
    // EXTERNAL stop (logout/reset) during the flush must win — bail instead
    // of resurrecting an engine the user tore down.
    if (!this.running) return
    this.stop()
    this.running = true // our own stop() cleared it; the engine is NOT logged out.
    const invalidationGeneration = this.startGeneration

    // 3. Settle in-flight resyncs AND floating transport applies BEFORE
    // wiping: neither checks the start generation, so a delta apply or a
    // large replay-batch `putMany` landing after the wipe would resurrect
    // stale rows and re-write the `_syncMeta` cursor — making `start()`
    // silently skip the cold re-pull. `stop()` above already detached the
    // transport handler, so no NEW applies can spawn — but a settling apply's
    // `.catch` can still spawn a RECOVERY resync (and a settling resync a
    // follow-up), which a single snapshot would miss (cubic P1). Loop to
    // quiescence: each round re-snapshots the union; the spawn chain is
    // finite once the handler is detached, so this converges in 2-3 rounds.
    for (let settleRound = 0; settleRound < 10; settleRound++) {
      const pending = [
        ...this.resyncInFlight.values(),
        ...this.transportApplyInFlight,
      ]
      if (pending.length === 0) break
      await Promise.allSettled(pending)
    }
    if (this.resyncInFlight.size + this.transportApplyInFlight.size > 0) {
      // 10 rounds without quiescence — never observed; surface it loudly
      // rather than wipe under a live writer (§2.1.8).
      console.error(
        "[SyncEngine] Scope invalidation proceeding with unsettled work:",
        this.resyncInFlight.size,
        "resyncs,",
        this.transportApplyInFlight.size,
        "applies"
      )
    }

    // 4. Wipe group-scoped resources only. Company-wide / per-user tables
    // (products, partners, chat-*, …) survive untouched.
    await this.wipeGroupScopedResources()

    // 5. Persist the new version BEFORE reconnecting, so the reconnect
    // handshake's `scope` frame compares equal and doesn't re-trigger. This
    // put also CLEARS the durable `pending` marker (whole-row overwrite).
    await this.storage.put(SYNC_META_TABLE, { id: SCOPE_META_ID, v })

    // 6. Standard start(): cold-start waves re-pull exactly the wiped set
    // (no `_syncMeta` row ⇒ in-wave), SSE reconnects with fresh cursors and
    // the server re-snapshots the connection's groupIds. Skipped if an
    // external stop (logout) landed during the wipe — its generation bump
    // and `running=false` both mark the teardown as the newer intent.
    if (!this.running || this.startGeneration !== invalidationGeneration) {
      return
    }
    await this.start()

    this.telemetry?.track("sync.scope_invalidation.completed", {
      durationMs: Date.now() - startedAt,
    })
  }

  /**
   * Surgical per-resource resync via `GET /:resource/changes?since=N`.
   * Triggered by:
   *   1. Gap detected on a live SSE event (`prevSyncId` ≠ local cursor).
   *   2. Push response's `latestSyncId` ahead of local cursor (piggyback
   *      safety net for the case where the SSE listener never received
   *      the event at all — silent drop, throttled tab, etc.).
   *
   * Concurrent triggers for the same resource are coalesced into the same
   * in-flight promise. Live events for the resource are dropped while the
   * fetch is running (they're included in the response).
   *
   * Pagination follows the standard `hasMore` protocol — the loop advances
   * `since` to the response's `latestSyncId` until no more pages remain.
   */
  private async resyncResource(
    config: OfflineResourceConfig,
    sinceOverride?: number
  ): Promise<void> {
    const existing = this.resyncInFlight.get(config.name)
    if (existing) {
      // Coalesced onto an in-flight resync. That resync's snapshot may PREDATE
      // the state this caller is chasing — a 409 self-heal or push piggyback
      // fires against rows the server committed AFTER the in-flight GET started,
      // so the coalesced promise can resolve without ever pulling them, making
      // the heal a silent no-op. Record a request so the in-flight resync's
      // `finally` runs one more pass from the (now-settled) cursor.
      //
      // Skipped for `sinceOverride` (shielded-drop recovery): that path can only
      // be satisfied by a resync from its own lower floor, which it re-schedules
      // itself via `recoverShieldedDrops` — a plain follow-up from the cursor
      // can't re-fetch below it. (It also guards on `resyncInFlight` before
      // calling, so it never reaches here in practice.)
      if (sinceOverride === undefined) this.resyncRequested.add(config.name)
      return existing
    }

    const promise = (async () => {
      this.resyncing.add(config.name)
      const startedAt = Date.now()
      this.telemetry?.track("sync.resync.started", { resource: config.name })
      try {
        // `sinceOverride` (shielded-drop recovery) re-fetches from below the
        // current cursor; the `updateSyncMeta` MAX guard keeps the persisted
        // cursor from regressing while the re-applied ops land authoritative.
        let since = sinceOverride ?? this.cursorCache[config.name] ?? 0
        let hasMore = true
        while (hasMore) {
          const response = await this.httpClient.get<
            SyncPullResponse<{ id: string }>
          >(config.endpoints.delta, { since: since.toString() })
          await this.applyOperations(config, response.operations)
          if (response.latestSyncId > since) {
            await this.updateSyncMeta(config.name, response.latestSyncId)
            since = response.latestSyncId
          } else if (response.hasMore) {
            // Server claims more pages but didn't advance the cursor — sending
            // the same `since` again would loop forever and pin `resyncing`,
            // silently dropping all live SSE events for this resource. Bail;
            // the next SSE reconnect's replay handshake will catch us up.
            console.warn(
              `[SyncEngine] Resync for "${config.name}" aborted: server returned hasMore=true but latestSyncId (${response.latestSyncId}) did not advance past since (${since}).`
            )
            break
          }
          hasMore = response.hasMore
        }
        this.telemetry?.track("sync.resync.succeeded", {
          resource: config.name,
          durationMs: Date.now() - startedAt,
        })
      } catch (err) {
        // A closed DB is expected during the logout/reset wipe and is recovered
        // by the `onClose` watcher when it happens unexpectedly — swallow it
        // (writing to the same closed store can't succeed) rather than report a
        // resync failure or escalate to every caller's error log.
        if (isDbClosedError(err)) return
        this.telemetry?.track("sync.resync.failed", {
          resource: config.name,
          durationMs: Date.now() - startedAt,
          error: err instanceof Error ? err.message : String(err),
        })
        throw err
      } finally {
        this.resyncing.delete(config.name)
        this.resyncInFlight.delete(config.name)

        // At most ONE follow-up resync fires per settle. Three independent
        // triggers can require one:
        //   (a) live-drop watermark — a live op was dropped at :720 while this
        //       resync ran and the settled cursor never reached its syncId, so
        //       the resync's snapshot predated it.
        //   (b) coalesced request — requestResync / notifyServerSyncId arrived
        //       mid-flight and got this (possibly stale) coalesced promise; it
        //       needs a fresh pass from the now-settled cursor.
        //   (c) shield-floor recovery — owned by `recoverShieldedDrops`, which
        //       resyncs from a LOWER floor and therefore re-fetches EVERY op
        //       above it, subsuming (a) and (b). When a floor is set we run it
        //       ALONE: a parallel plain follow-up would re-arm `resyncInFlight`
        //       and make `recoverShieldedDrops` back off, needlessly deferring
        //       the floor recovery by a cycle.
        const watermark = this.resyncDropWatermark.get(config.name)
        // Clear the watermark FIRST so any follow-up's own `finally` re-arms it
        // only on a genuinely newer drop — never an infinite loop. A transient
        // follow-up failure re-arms it in the `.catch` below.
        if (watermark !== undefined)
          this.resyncDropWatermark.delete(config.name)
        const reRequested = this.resyncRequested.delete(config.name)
        const watermarkMissed =
          watermark !== undefined &&
          (this.cursorCache[config.name] ?? 0) < watermark

        if (this.shieldRefetchFloor.has(config.name)) {
          // Floor resync (from below the cursor) subsumes the dropped live op
          // and any coalesced request; the floor is its own durable backstop,
          // re-armed on failure inside `recoverShieldedDrops`.
          void this.recoverShieldedDrops(config).catch((err) => {
            console.error(
              `[SyncEngine] Shielded-drop recovery failed for "${config.name}":`,
              err
            )
          })
        } else if (watermarkMissed || reRequested) {
          // Plain follow-up from the current cursor pulls the missed live op /
          // the rows a coalesced 409-heal or piggyback was chasing.
          void this.resyncResource(config).catch((err) => {
            // Re-arm the triggers so a transient follow-up failure still retries
            // on the next settle instead of silently dropping the recovery —
            // mirrors the shield-floor re-arm in `recoverShieldedDrops`.
            if (watermark !== undefined) {
              const cur = this.resyncDropWatermark.get(config.name)
              this.resyncDropWatermark.set(
                config.name,
                Math.max(cur ?? 0, watermark)
              )
            }
            if (reRequested) this.resyncRequested.add(config.name)
            console.error(
              `[SyncEngine] Resync-drop recovery failed for "${config.name}":`,
              err
            )
          })
        }
      }
    })()

    this.resyncInFlight.set(config.name, promise)
    return promise
  }

  /**
   * Push piggyback: a successful mutation response carries the server's current
   * user-visible `latestSyncId` for the affected resource. If it's ahead of our
   * local cursor we missed a live SSE event — trigger a surgical resync to pull
   * the rows (NOT a blind cursor bump; advancing without fetching would leave
   * Dexie stale — see the same reasoning at the push-drain piggyback). No-op if
   * the id isn't finite or isn't ahead of the cursor.
   */
  notifyServerSyncId(resource: string, latestSyncId: number | string): void {
    // BIGSERIAL syncIds round-trip through JSON as strings — coerce before the
    // numeric compare so a stringified id isn't treated as always-ahead.
    const n = Number(latestSyncId)
    if (!Number.isFinite(n)) return
    if (n <= (this.cursorCache[resource] ?? 0)) return
    this.triggerResync(resource)
  }

  /**
   * Unconditional surgical resync for a resource, regardless of cursor. Used by
   * the push-batch 409 staleness path (`export_already_succeeded` /
   * `export_in_progress`), where the server has a newer authoritative state but
   * the 409 body carries no syncId to compare against. `resyncInFlight`
   * coalesces repeated calls so hammering the button can't fan out parallel GETs.
   */
  requestResync(resource: string): void {
    this.triggerResync(resource)
  }

  /** Fire-and-forget resync for a registered resource (no-op if unknown). */
  private triggerResync(resource: string): void {
    const config = this.resources.get(resource)
    if (!config) return
    void this.resyncResource(config).catch((err) => {
      console.error(
        `[SyncEngine] Requested resync failed for "${resource}":`,
        err
      )
    })
  }

  /**
   * Recover server ops the in-flight-write shield dropped (see
   * `applyOperations`). Runs after a queued change settles (push ack OR
   * permanent failure). Waits until no active (pending/syncing) change still
   * shields the resource — otherwise the recovery resync would re-drop and
   * loop — then resyncs from the recorded floor to pull authoritative state.
   * The floor stays set if the resync is coalesced away, so it retries on the
   * next settle.
   */
  private async recoverShieldedDrops(
    config: OfflineResourceConfig
  ): Promise<void> {
    const floor = this.shieldRefetchFloor.get(config.name)
    if (floor === undefined) return
    const stillShielded = await this.changeQueue.getActiveEntityIds(config.name)
    if (stillShielded.size > 0) return
    if ((this.transientShield.get(config.name)?.size ?? 0) > 0) return
    // A resync already running was started from the normal cursor, NOT our
    // lower floor, so it can't re-fetch the dropped op. Leave the floor set and
    // retry on the next settle rather than clearing it against a resync that
    // can't recover. (Checked last — right before the synchronous start of
    // `resyncResource`, with no await in between, so it won't coalesce.)
    if (this.resyncInFlight.has(config.name)) return
    this.shieldRefetchFloor.delete(config.name)
    try {
      await this.resyncResource(config, floor)
    } catch (err) {
      // Re-arm the floor so a transient resync failure still retries later.
      const cur = this.shieldRefetchFloor.get(config.name)
      if (cur === undefined || floor < cur) {
        this.shieldRefetchFloor.set(config.name, floor)
      }
      throw err
    }
  }

  /**
   * Transient in-flight shield for change-queue-bypassing writes (pushBatch's
   * optimistic apply). The caller registers the entity ids it is about to write
   * so `applyOperations` won't let a late SSE echo clobber them before the POST
   * reconciles; `releaseEntities` decrements the matching counts afterward.
   */
  shieldEntities(resource: string, ids: string[]): void {
    if (ids.length === 0) return
    let counts = this.transientShield.get(resource)
    if (!counts) {
      counts = new Map<string, number>()
      this.transientShield.set(resource, counts)
    }
    for (const id of ids) {
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }

  /** Release a transient shield and recover anything dropped while it was up. */
  releaseEntities(resource: string, ids: string[]): void {
    const counts = this.transientShield.get(resource)
    if (!counts) return
    for (const id of ids) {
      const next = (counts.get(id) ?? 0) - 1
      if (next > 0) {
        counts.set(id, next)
      } else {
        counts.delete(id)
      }
    }
    if (counts.size === 0) this.transientShield.delete(resource)
    const config = this.resources.get(resource)
    if (config) {
      void this.recoverShieldedDrops(config).catch((err) => {
        console.error(
          `[SyncEngine] Shielded-drop recovery failed for "${resource}":`,
          err
        )
      })
    }
  }

  private async getSyncMeta(resource: string): Promise<SyncMeta | null> {
    return this.storage.get<SyncMeta>(SYNC_META_TABLE, resource)
  }

  private async saveSyncMeta(resource: string, meta: SyncMeta): Promise<void> {
    // SyncMeta uses resource name as id for storage adapter
    await this.storage.put(SYNC_META_TABLE, {
      ...meta,
      id: resource,
    })
  }

  /**
   * Advance the body-backfill watermark WITHOUT clobbering `lastSyncId`.
   *
   * `_syncMeta` is a single row per resource, so every writer does
   * read-modify-write on the whole row. The backfill runs concurrently with
   * delta/SSE applies (`updateSyncMeta`), so it MUST re-read immediately before
   * writing and preserve the freshest `lastSyncId` — spreading a `meta` snapshot
   * read before the page fetch would regress `lastSyncId` back to a stale value
   * and make the next replay handshake re-process already-applied changes.
   * `bodyCursor` itself only ever ADVANCES (MAX guard), so an initial pass and
   * a reconnect re-kick can't regress each other.
   */
  private async saveBodyCursor(
    resource: string,
    bodyCursor: number
  ): Promise<void> {
    const existing = await this.getSyncMeta(resource)
    if (!existing) return
    await this.saveSyncMeta(resource, {
      ...existing,
      bodyCursor: Math.max(existing.bodyCursor ?? 0, bodyCursor),
    })
  }

  private async updateSyncMeta(
    resource: string,
    latestSyncId: number
  ): Promise<void> {
    const existing = await this.getSyncMeta(resource)
    // MAX guard: a live event with a newer syncId can race the replay batch
    // (which carries the older `latestSyncId` snapshot). Don't regress the
    // cursor — otherwise the next reconnect's handshake would re-replay
    // events we've already applied.
    const winner = Math.max(existing?.lastSyncId ?? 0, latestSyncId)
    // Per-resource status reflects data readiness, not engine connection state.
    // A successful pull/SSE apply means this resource has data the UI can use.
    await this.saveSyncMeta(resource, {
      resource,
      lastSyncId: winner,
      lastFullFetchAt: existing?.lastFullFetchAt ?? null,
      status: "online",
      // Delta/SSE updates advance the HIGH cursor only — the windowed low
      // watermark is untouched (eviction/floor-refresh own it).
      windowFloor: existing?.windowFloor,
      bodyCursor: existing?.bodyCursor,
    })
    this.cursorCache[resource] = winner
  }
}
